import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ProductCard from '../components/ProductCard';
import { getProducts } from '../services/firestore';

const Products = () => {
  const { t } = useTranslation();
  const [selectedSection, setSelectedSection] = useState('weapons');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch products from Firestore
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const productsData = await getProducts();
      setProducts(productsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(`Failed to load products: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Initialize component
  useEffect(() => {
    const initializeProducts = async () => {
      try {
        // Fetch products on component mount
        await fetchProducts();
      } catch (error) {
        console.error('Error in initialization:', error);
        setError(`Initialization failed: ${error.message}`);
        setLoading(false);
      }
    };
    
    initializeProducts();
  }, []);

  // Filter products by category
  const weaponsData = products.filter(product => product.category === 'weapons');
  const ammunitionData = products.filter(product => product.category === 'ammunition');

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-military-accent mx-auto mb-4"></div>
          <p className="text-military-silver font-military-mono">LOADING PRODUCTS...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen flex items-center justify-center bg-black">
        <div className="text-center max-w-md mx-auto">
          <div className="text-6xl mb-4">⚠️</div>
          <h3 className="text-2xl font-black text-gradient-military mb-2">ERROR</h3>
          <p className="text-military-silver mb-6 font-military-mono">{error}</p>
          
          <div className="space-y-3">
            <button
              onClick={() => fetchProducts()}
              className="w-full btn-military-primary"
            >
              RETRY
            </button>
            
            <button
              onClick={() => window.location.reload()}
              className="w-full btn-military-secondary"
            >
              REFRESH PAGE
            </button>
          </div>
          
          <div className="mt-6 text-sm text-military-silver font-military-mono">
            <p>If the problem persists, please contact our support team.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20">
      {/* Military Hero Section */}
      <section className="section-military bg-black">
        <div className="container-military text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-black tracking-tighter mb-8 text-gradient-military"
          >
            {t('weapons.page.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-military-silver max-w-3xl mx-auto font-military-mono"
          >
            {t('weapons.page.subtitle')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="w-32 h-1 bg-gradient-to-r from-military-accent to-military-accent mx-auto mt-6"
          />
        </div>
      </section>

      {/* Section Tabs */}
      <section className="section-military bg-gray-900">
        <div className="container-military">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="flex justify-center gap-4 mb-8">
              <button
                onClick={() => setSelectedSection('weapons')}
                className={`px-8 py-4 font-bold tracking-wider uppercase transition-all duration-300 text-lg ${
                  selectedSection === 'weapons'
                    ? 'bg-military-accent text-black'
                    : 'bg-black/50 border-2 border-military-accent text-military-accent hover:bg-military-accent hover:text-black'
                }`}
              >
                {t('weapons.sections.weapons.title')}
              </button>
              <button
                onClick={() => setSelectedSection('ammunition')}
                className={`px-8 py-4 font-bold tracking-wider uppercase transition-all duration-300 text-lg ${
                  selectedSection === 'ammunition'
                    ? 'bg-military-accent text-black'
                    : 'bg-black/50 border-2 border-military-accent text-military-accent hover:bg-military-accent hover:text-black'
                }`}
              >
                {t('weapons.sections.ammunition.title')}
              </button>
            </div>
          </motion.div>

          {/* Weapons Section */}
          {selectedSection === 'weapons' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
                             <div className="text-center mb-12">
                 <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-4">
                   {t('weapons.sections.weapons.title')}
                 </h2>
                 <p className="text-lg text-military-silver max-w-2xl mx-auto font-military-mono">
                   {t('weapons.page.subtitle')}
                 </p>
               </div>

                               {weaponsData.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {weaponsData.map((weapon, index) => (
                      <motion.div
                        key={weapon.id}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        className="military-card cursor-pointer group"
                        onClick={() => handleProductClick(weapon)}
                      >
                        <div className="aspect-video bg-gray-800 mb-4 rounded overflow-hidden">
                          <img 
                            src={weapon.imageUrl || weapon.imageUrls?.[0] || '/assets/placeholder-image.jpg'} 
                            alt={weapon.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <h3 className="text-xl font-black tracking-tighter text-gradient-military mb-3">
                          {weapon.name}
                        </h3>
                        <p className="text-military-silver font-military-mono text-sm mb-4">
                          {weapon.description || 'Advanced military equipment designed for superior performance.'}
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {weapon.specs && Object.entries(weapon.specs).slice(0, 6).map(([key, value]) => (
                            <React.Fragment key={key}>
                              <div className="text-military-accent font-bold">{key.toUpperCase()}</div>
                              <div className="text-military-silver">{value}</div>
                            </React.Fragment>
                          ))}
                          {(!weapon.specs || Object.keys(weapon.specs).length === 0) && (
                            <>
                              <div className="text-military-accent font-bold">CATEGORY</div>
                              <div className="text-military-silver">{weapon.category}</div>
                            </>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🔫</div>
                                      <h3 className="text-2xl font-black text-gradient-military mb-2">{t('weapons.noWeapons')}</h3>
                  <p className="text-military-silver font-military-mono">No weapons have been added to this category yet.</p>
                  </div>
                )}
              </motion.div>
            )}

          {/* Ammunition Section */}
          {selectedSection === 'ammunition' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-4">
                  SPECIALIZED AMMUNITION
                </h2>
                <p className="text-lg text-military-silver max-w-2xl mx-auto font-military-mono">
                  High-performance ammunition engineered for specific tactical requirements and mission objectives.
                </p>
              </div>

              {ammunitionData.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {ammunitionData.map((ammo, index) => (
                    <motion.div
                      key={ammo.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="military-card cursor-pointer group"
                      onClick={() => handleProductClick(ammo)}
                    >
                      <div className="aspect-video bg-gray-800 mb-4 rounded overflow-hidden">
                        <img 
                          src={ammo.imageUrl || ammo.imageUrls?.[0] || '/assets/placeholder-image.jpg'} 
                          alt={ammo.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <h3 className="text-xl font-black tracking-tighter text-gradient-military mb-3">
                        {ammo.name}
                      </h3>
                      <p className="text-military-silver font-military-mono text-sm mb-4">
                        {ammo.description || 'High-performance ammunition engineered for specific tactical requirements.'}
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {ammo.specs && Object.entries(ammo.specs).slice(0, 6).map(([key, value]) => (
                          <React.Fragment key={key}>
                            <div className="text-military-accent font-bold">{key.toUpperCase()}</div>
                            <div className="text-military-silver">{value}</div>
                          </React.Fragment>
                        ))}
                        {(!ammo.specs || Object.keys(ammo.specs).length === 0) && (
                          <>
                            <div className="text-military-accent font-bold">CATEGORY</div>
                            <div className="text-military-silver">{ammo.category}</div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">💥</div>
                  <h3 className="text-2xl font-black text-gradient-military mb-2">{t('weapons.noAmmunition')}</h3>
                  <p className="text-military-silver font-military-mono">No ammunition has been added to this category yet.</p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* Technology Overview */}
      <section className="section-military bg-black">
        <div className="container-military">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
                         <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-6">
               {t('weapons.technology.title')}
             </h2>
             <p className="text-lg md:text-xl text-military-silver max-w-3xl mx-auto font-military-mono">
               {t('weapons.technology.subtitle')}
             </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="military-card text-center p-6 md:p-8"
            >
              <div className="w-16 h-16 bg-military-accent rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🧠</span>
              </div>
                             <h3 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-4">
                 {t('weapons.technology.ai.title')}
               </h3>
               <p className="text-military-silver font-military-mono text-sm md:text-base">
                 {t('weapons.technology.ai.description')}
               </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="military-card text-center p-6 md:p-8"
            >
              <div className="w-16 h-16 bg-military-accent rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🔒</span>
              </div>
                             <h3 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-4">
                 {t('weapons.technology.cybersecurity.title')}
               </h3>
               <p className="text-military-silver font-military-mono text-sm md:text-base">
                 {t('weapons.technology.cybersecurity.description')}
               </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="military-card text-center p-6 md:p-8"
            >
              <div className="w-16 h-16 bg-military-accent rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">⚡</span>
              </div>
                             <h3 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-4">
                 {t('weapons.technology.performance.title')}
               </h3>
               <p className="text-military-silver font-military-mono text-sm md:text-base">
                 {t('weapons.technology.performance.description')}
               </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section-military bg-gray-900">
        <div className="container-military text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
                         <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-8">
               {t('weapons.cta.title')}
             </h2>
             <p className="text-lg md:text-xl text-military-silver mb-12 max-w-2xl mx-auto font-military-mono">
               {t('weapons.cta.subtitle')}
             </p>
             <a href="/contact" className="btn-military-primary">
               {t('weapons.cta.button')}
             </a>
          </motion.div>
        </div>
      </section>

      {/* Product Detail Modal */}
      {showModal && selectedProduct && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-black border-2 border-military-accent max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-3xl font-black tracking-tighter text-gradient-military">
                  {selectedProduct.name}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-military-accent hover:text-white transition-colors duration-300 text-2xl"
                >
                  ×
                </button>
              </div>

                             <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                 <div className="aspect-video bg-gray-800 rounded overflow-hidden">
                   <img 
                     src={selectedProduct.imageUrl || selectedProduct.imageUrls?.[0] || '/assets/placeholder-image.jpg'} 
                     alt={selectedProduct.name}
                     className="w-full h-full object-cover"
                   />
                 </div>

                 <div>
                   <p className="text-military-silver font-military-mono mb-6">
                     {selectedProduct.description || 'Advanced military equipment with superior performance characteristics.'}
                   </p>

                  <h3 className="text-xl font-black tracking-tighter text-gradient-military mb-4">
                    TECHNICAL SPECIFICATIONS
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(selectedProduct.specs).map(([key, value]) => (
                      <div key={key} className="border-b border-military-accent/30 pb-2">
                        <div className="text-military-accent font-bold text-sm uppercase tracking-wider">
                          {key.replace(/([A-Z])/g, ' $1').toUpperCase()}
                        </div>
                        <div className="text-military-silver font-military-mono">
                          {value}
                        </div>
                      </div>
                    ))}
                  </div>

                  
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Products; 